import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { ImprovementArea } from '../types';

// Define MaturityLevel type based on the values used in the component
type MaturityLevel = 'Weak' | 'Emerging' | 'Established' | 'Peak' | null;

// Helper function to render maturity level as squares
function renderMaturityAsSquares(level: MaturityLevel, color: string): string {
  // Define the total number of squares
  const totalSquares = 4;

  // Handle null maturity level
  if (level === null) {
    // Generate all grey squares for null maturity
    let squaresHtml = '';
    for (let i = 0; i < totalSquares; i++) {
      squaresHtml += `<div style="width: 8px; height: 8px; background-color: #d1d5db; margin-right: 2px;"></div>`;
    }
    return `<div style="display: flex; align-items: center;">
              <div style="display: flex; margin-right: 5px;">${squaresHtml}</div>
              <span style="color: #9ca3af;">Not assessed</span>
            </div>`;
  }

  // Determine how many colored squares to show based on level
  let coloredSquares = 0;
  if (level === 'Weak') coloredSquares = 1;
  else if (level === 'Emerging') coloredSquares = 2;
  else if (level === 'Established') coloredSquares = 3;
  else if (level === 'Peak') coloredSquares = 4;

  // Generate HTML for the squares
  let squaresHtml = '';
  for (let i = 0; i < totalSquares; i++) {
    const squareColor = i < coloredSquares ? color : '#d1d5db';
    squaresHtml += `<div style="width: 8px; height: 8px; background-color: ${squareColor}; margin-right: 2px;"></div>`;
  }

  // Always return both the squares and the text
  return `<div style="display: flex; align-items: center;">
            <div style="display: flex; margin-right: 5px;">${squaresHtml}</div>
            <span>${level}</span>
          </div>`;
}

interface D3BubbleChartProps {
  areas: ImprovementArea[];
  onMaturityChange: (areaName: string, newLevel: MaturityLevel) => void;
  highlightedAreas: string[];
  relationshipWeights: { [key: string]: number };
  getBackgroundColor: (maturity: MaturityLevel) => string;
  getBorderColor: (maturity: MaturityLevel) => string;
  onHover?: (areaName: string | null, hovering: boolean) => void;
  quadrantDescriptions?: { [key: string]: string };
}

export const D3BubbleChart: React.FC<D3BubbleChartProps> = ({
  areas,
  onMaturityChange,
  highlightedAreas,
  relationshipWeights,
  getBackgroundColor,
  getBorderColor,
  onHover,
  quadrantDescriptions = {},
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const positionsRef = useRef<{
    [key: string]: { x: number; y: number; r: number };
  }>({});
  const visibleLabelsRef = useRef<Set<string>>(new Set());
  const openDialogRef = useRef<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 1000, height: 800 });
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const padding = 60;

  // Add the logo import at the top level
  const logoUrl = '/logo.svg';

  // Define quadrant colors
  const quadrantColors = useMemo(() => ['#4a9cde', '#b55dd7', '#3eb489', '#e94f59'], []);

  // Function to get quadrant color by area name
  const getQuadrantColor = useCallback(
    (areaName: string): string => {
      // Find the area in the areas array
      const area = areas.find(a => a.name === areaName);
      if (!area) return '#888888'; // Default gray if not found

      // Get quadrant index
      let quadrantIndex = 0;
      const quadrants = ['Craftsmanship', 'Innovation', 'Collaboration', 'Delivery'];
      const index = quadrants.indexOf(area.quadrant);
      if (index !== -1) {
        quadrantIndex = index;
      }

      return quadrantColors[quadrantIndex];
    },
    [areas, quadrantColors],
  );

  // Update dimensions when the window size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: width,
          height: height,
        });
        setIsSmallScreen(width < 800);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Calculate and store positions only when dimensions or area set changes
  useEffect(() => {
    // Group areas by quadrant
    const areasByQuadrant = areas.reduce((acc, area) => {
      if (!acc[area.quadrant]) {
        acc[area.quadrant] = [];
      }
      acc[area.quadrant].push(area);
      return acc;
    }, {} as { [key: string]: ImprovementArea[] });

    // Calculate quadrant dimensions
    const quadrantWidth = (dimensions.width - padding * 2) / 2;
    const quadrantHeight = (dimensions.height - padding * 2) / 2;

    // Clear and recalculate positions
    const newPositions: { [key: string]: { x: number; y: number; r: number } } = {};

    Object.entries(areasByQuadrant).forEach(([quadrant, quadrantAreas], quadrantIndex) => {
      const isLeft = quadrantIndex % 2 === 0;
      const isTop = quadrantIndex < 2;

      // Calculate quadrant center
      const centerX = isLeft ? padding + quadrantWidth / 2 : dimensions.width - padding - quadrantWidth / 2;
      const centerY = isTop ? padding + quadrantHeight / 2 : dimensions.height - padding - quadrantHeight / 2;

      // Create nodes data with positions
      const nodes = quadrantAreas.map(area => ({
        ...area,
        // For null maturity levels, use a reasonable default size based on average impact
        value:
          area.maturityLevel === null
            ? 300 // Give null maturity areas a reasonable size
            : Math.max(350, Math.pow(area.calculatedImpact, 1.5) * 65),
        maturityLevel: area.maturityLevel, // Keep null values as-is for proper handling
      }));

      // Create hierarchical data structure for circle packing
      const hierarchyData = {
        name: quadrant,
        children: nodes,
      };

      // Calculate the containing circle size - increase to use more space
      const containingSize = Math.min(quadrantWidth, quadrantHeight) * 0.95;

      // Create a hierarchy for circle packing
      const root = d3
        .hierarchy(hierarchyData)
        .sum((d: any) => d.value)
        .sort((a: any, b: any) => (b.value || 0) - (a.value || 0));

      // Determine if we're on a small screen
      const isSmallScreen = dimensions.width < 800;

      // Create the pack layout
      const pack = d3
        .pack()
        .size([containingSize, containingSize])
        .padding(isSmallScreen ? 10 : 15);

      // Calculate positions
      const packedData = pack(root);
      packedData.leaves().forEach((d: any) => {
        const minRadius = isSmallScreen ? 30 : 0;
        const calculatedRadius = d.r;
        const finalRadius = Math.max(minRadius, calculatedRadius);
        newPositions[d.data.name] = {
          x: d.x + centerX - packedData.r,
          y: d.y + centerY - packedData.r,
          r: finalRadius,
        };
      });
    });
    positionsRef.current = newPositions;
  }, [dimensions.width, dimensions.height, areas]);

  useEffect(() => {
    if (!svgRef.current) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Remember which circles had visible labels before rendering
    const previouslyVisibleLabels = new Set(visibleLabelsRef.current);

    // Create the SVG container
    const svg = d3.select(svgRef.current).attr('width', dimensions.width).attr('height', dimensions.height);

    // Create clean background with distinct quadrant colors instead of blurry gradient
    const bgGroup = svg.append('g').attr('class', 'background-group');

    // Create gradient for the main outer circle border
    const outerBorderGradientId = 'main-border-gradient';
    const outerDefs = svg.append('defs');

    // Add a drop shadow filter for the inner circle
    const dropShadowId = 'inner-circle-shadow';
    outerDefs
      .append('filter')
      .attr('id', dropShadowId)
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%')
      .append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '3')
      .attr('stdDeviation', '4')
      .attr('flood-opacity', '0.2')
      .attr('flood-color', 'rgb(0,0,0)');

    // Add an inner shadow filter for the logo
    const innerShadowId = 'logo-inner-shadow';
    const innerShadowFilter = outerDefs
      .append('filter')
      .attr('id', innerShadowId)
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');

    // Inner shadow components
    innerShadowFilter.append('feOffset').attr('dx', '0').attr('dy', '3');

    innerShadowFilter.append('feGaussianBlur').attr('stdDeviation', '4').attr('result', 'blur');

    innerShadowFilter
      .append('feComposite')
      .attr('in', 'SourceGraphic')
      .attr('in2', 'blur')
      .attr('operator', 'out')
      .attr('result', 'composite1');

    innerShadowFilter.append('feFlood').attr('flood-color', 'rgba(0,0,0,0.25)').attr('result', 'color');

    innerShadowFilter
      .append('feComposite')
      .attr('in', 'color')
      .attr('in2', 'composite1')
      .attr('operator', 'in')
      .attr('result', 'shadow');

    innerShadowFilter.append('feComposite').attr('in', 'shadow').attr('in2', 'SourceGraphic').attr('operator', 'over');

    // Add gradient definition with more visibility
    const outerGradient = outerDefs
      .append('linearGradient')
      .attr('id', outerBorderGradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');

    // Add gradient stops for each quadrant color
    outerGradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#4a9cde') // Brighter blue (top-left)
      .attr('stop-opacity', 0.8);

    outerGradient
      .append('stop')
      .attr('offset', '25%')
      .attr('stop-color', '#b55dd7') // Brighter purple (top-right)
      .attr('stop-opacity', 0.8);

    outerGradient
      .append('stop')
      .attr('offset', '50%')
      .attr('stop-color', '#e94f59') // Brighter red (bottom-right)
      .attr('stop-opacity', 0.8);

    outerGradient
      .append('stop')
      .attr('offset', '75%')
      .attr('stop-color', '#3eb489') // Brighter green (bottom-left)
      .attr('stop-opacity', 0.8);

    outerGradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#4a9cde') // Back to blue to complete the circle
      .attr('stop-opacity', 0.8);

    // Add a subtle white background circle with gradient border
    bgGroup
      .append('circle')
      .attr('cx', dimensions.width / 2)
      .attr('cy', dimensions.height / 2)
      .attr('r', (Math.min(dimensions.width, dimensions.height) / 2 - padding / 2) * 0.7) // 30% smaller
      .style('fill', '#f8f8f8') // Changed from 'white' to match the logo circle
      .style('stroke', `url(#${outerBorderGradientId})`) // Apply gradient to the border
      .style('stroke-width', '12px') // Increased by 30% (was 9px)
      .style('filter', `url(#${dropShadowId})`); // Apply drop shadow

    // Add a grey dotted circle around the gradient circle
    bgGroup
      .append('circle')
      .attr('cx', dimensions.width / 2)
      .attr('cy', dimensions.height / 2)
      .attr('r', (Math.min(dimensions.width, dimensions.height) / 2 - padding / 2) * 0.75) // Slightly larger than gradient circle
      .style('fill', 'none')
      .style('stroke', '#9ca3af') // Medium grey color
      .style('stroke-width', '1px')
      .style('stroke-dasharray', '2,8'); // Smaller dots with larger gaps

    // Add subtle decorative elements - using the component-level quadrantColors variable
    // (removing redundant declaration)

    // Add subtle color accents to each quadrant
    [0, 1, 2, 3].forEach(quadIndex => {
      const isLeft = quadIndex % 2 === 0;
      const isTop = quadIndex < 2;

      const centerX = isLeft ? dimensions.width / 4 : (dimensions.width * 3) / 4;
      const centerY = isTop ? dimensions.height / 4 : (dimensions.height * 3) / 4;

      // Create subtle quadrant color indicator
      bgGroup
        .append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', Math.min(dimensions.width, dimensions.height) / 10)
        .style('fill', 'none')
        .style('stroke', quadrantColors[quadIndex])
        .style('stroke-width', '2px')
        .style('stroke-opacity', 0.15);
    });

    // Group areas by quadrant
    const areasByQuadrant = areas.reduce((acc, area) => {
      if (!acc[area.quadrant]) {
        acc[area.quadrant] = [];
      }
      acc[area.quadrant].push(area);
      return acc;
    }, {} as { [key: string]: ImprovementArea[] });

    // Calculate quadrant dimensions
    const quadrantWidth = (dimensions.width - padding * 2) / 2;
    const quadrantHeight = (dimensions.height - padding * 2) / 2;

    // For each quadrant, render the circles using stored positions
    Object.entries(areasByQuadrant).forEach(([quadrant, quadrantAreas], quadrantIndex) => {
      const isLeft = quadrantIndex % 2 === 0;
      const isTop = quadrantIndex < 2;

      // Calculate quadrant center
      const centerX = isLeft ? padding + quadrantWidth / 2 : dimensions.width - padding - quadrantWidth / 2;
      const centerY = isTop ? padding + quadrantHeight / 2 : dimensions.height - padding - quadrantHeight / 2;

      // Create container for this quadrant
      const quadrantGroup = d3.select(svgRef.current).append('g').attr('class', `quadrant-${quadrantIndex}`);

      // Create nodes data with positions
      const nodes = quadrantAreas.map(area => ({
        ...area,
        // For null maturity levels, use a reasonable default size based on average impact
        value:
          area.maturityLevel === null
            ? 300 // Give null maturity areas a reasonable size
            : Math.max(350, Math.pow(area.calculatedImpact, 1.5) * 65),
        maturityLevel: area.maturityLevel, // Keep null values as-is for proper handling
      }));

      // Create hierarchical data structure for circle packing
      const hierarchyData = {
        name: quadrant,
        children: nodes,
      };

      // Calculate the containing circle size - increase to use more space
      const containingSize = Math.min(quadrantWidth, quadrantHeight) * 0.95;

      // Create a hierarchy for circle packing
      const root = d3
        .hierarchy(hierarchyData)
        .sum((d: any) => d.value)
        .sort((a: any, b: any) => (b.value || 0) - (a.value || 0));

      // Determine if we're on a small screen
      const isSmallScreen = dimensions.width < 800;

      // Create the pack layout
      const pack = d3
        .pack()
        .size([containingSize, containingSize])
        .padding(isSmallScreen ? 10 : 15);

      // Create the pack layout
      const packedData = pack(root);

      // Add shadow/background circle to create stacked effect
      quadrantGroup
        .append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY + 3) // Reduced offset for less blur
        .attr('r', packedData.r)
        .style('fill', () => {
          // Use a lighter version of the quadrant color
          const color = quadrantColors[quadrantIndex];
          // Convert hex to rgba with 0.15 opacity
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, 0.15)`;
        })
        .style('opacity', 0.9)
        .style('filter', 'blur(1px)'); // Less blur for sharper appearance

      // Draw just the quarter-circle curved border without inner radius
      // Calculate the radius for the border (25% larger than the quadrant circle)
      const borderRadius = packedData.r * 1.1;

      // Calculate angle values for the quarter arc
      let startAngle, endAngle;

      if (quadrant === 'Craftsmanship') {
        // Top-left
        startAngle = Math.PI * 1.0; // 180 degrees
        endAngle = Math.PI * 1.5; // 270 degrees
      } else if (quadrant === 'Innovation') {
        // Top-right
        startAngle = 0; // 0 degrees (where Delivery originally was)
        endAngle = Math.PI * 0.5; // 90 degrees (where Delivery originally was)
      } else if (quadrant === 'Delivery') {
        // Bottom-right
        startAngle = Math.PI * 1.5; // 270 degrees (same as Innovation was)
        endAngle = Math.PI * 2.0; // 360 degrees (same as Innovation was)
      } else {
        // Bottom-left (Collaboration)
        startAngle = Math.PI * 0.5; // 90 degrees
        endAngle = Math.PI * 1.0; // 180 degrees
      }

      // Create a path for just the curved border
      // Create an array of points along the arc
      const numPoints = 50; // More points = smoother curve
      const points = [];
      for (let i = 0; i <= numPoints; i++) {
        const angle = startAngle + (endAngle - startAngle) * (i / numPoints);
        const x = Math.cos(angle) * borderRadius;
        const y = Math.sin(angle) * borderRadius;
        points.push([x, y]);
      }

      // Create a line generator with curved interpolation
      const lineGenerator = d3.line().curve(d3.curveCardinal);

      // Create the path
      quadrantGroup
        .append('path')
        .attr('d', lineGenerator(points as any))
        .attr('transform', `translate(${centerX},${centerY})`)
        .style('fill', 'none')
        .style('stroke', '#999999')
        .style('stroke-width', '1px')
        .style('stroke-opacity', 0.7)
        .style('stroke-dasharray', '2,8') // Add dotted pattern like the other circle
        .style('filter', 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.08))');

      // Add quadrant boundary circle (using the root circle from pack layout)
      quadrantGroup
        .append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', packedData.r)
        .style('fill', 'white')
        .style('stroke', '#e5e7eb') // Simple gray border without gradient
        .style('stroke-width', '1px')
        .style('filter', 'drop-shadow(0px 3px 8px rgba(0, 0, 0, 0.12))');

      // Add a subtle inner circle decorative effect
      quadrantGroup
        .append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', packedData.r * 0.97) // Slightly smaller inner circle
        .style('fill', 'none')
        .style('stroke', '#f5f5f5') // Light grey instead of gradient
        .style('stroke-width', '1px');

      // Add small numbered label (01, 02, 03, 04) according to quadrant index
      const badgeSize = 20; // Slightly larger for better visibility

      // Position badge on the circle edge
      const badgeX = centerX + packedData.r - badgeSize * 1.5; // Moved further left by increasing multiplier from 1.2 to 1.5
      const badgeY = centerY; // Keep at the same vertical level as the circle center

      // Create the badge background with info icon
      quadrantGroup
        .append('circle')
        .attr('cx', badgeX)
        .attr('cy', badgeY)
        .attr('r', badgeSize)
        .style('fill', () => {
          // Use vibrant colors matching the quadrant
          return quadrantColors[quadrantIndex];
        })
        .style('filter', 'drop-shadow(0px 2px 3px rgba(0, 0, 0, 0.15))')
        .style('stroke', 'white')
        .style('stroke-width', '2px');

      // Add icon or question mark based on quadrant
      if (quadrant === 'Craftsmanship') {
        // Add craftsmanship icon image for the Craftsmanship quadrant
        const iconSize = badgeSize * 1.4; // Slightly larger than badge size for proper visual
        quadrantGroup
          .append('image')
          .attr('href', '/images/craftsmanship.png')
          .attr('width', iconSize)
          .attr('height', iconSize)
          .attr('x', badgeX - iconSize / 2)
          .attr('y', badgeY - iconSize / 2)
          .style('pointer-events', 'none');
      } else if (quadrant === 'Delivery') {
        // Add delivery icon image for the Delivery quadrant
        const iconSize = badgeSize * 1.2; // Reduced from 1.4 to 1.2 to make it smaller
        quadrantGroup
          .append('image')
          .attr('href', '/images/delivery.png')
          .attr('width', iconSize)
          .attr('height', iconSize)
          .attr('x', badgeX - iconSize / 2)
          .attr('y', badgeY - iconSize / 2)
          .style('pointer-events', 'none');
      } else if (quadrant === 'Innovation') {
        // Add innovation icon image for the Innovation quadrant
        const iconSize = badgeSize * 1.4;
        quadrantGroup
          .append('image')
          .attr('href', '/images/innovation.png')
          .attr('width', iconSize)
          .attr('height', iconSize)
          .attr('x', badgeX - iconSize / 2)
          .attr('y', badgeY - iconSize / 2)
          .style('pointer-events', 'none');
      } else if (quadrant === 'Collaboration') {
        // Add collaboration icon image for the Collaboration quadrant
        const iconSize = badgeSize * 1.4;
        quadrantGroup
          .append('image')
          .attr('href', '/images/collaboration.png')
          .attr('width', iconSize)
          .attr('height', iconSize)
          .attr('x', badgeX - iconSize / 2)
          .attr('y', badgeY - iconSize / 2)
          .style('pointer-events', 'none');
      } else {
        // Add question mark text for any other quadrants (fallback)
        quadrantGroup
          .append('text')
          .attr('x', badgeX)
          .attr('y', badgeY)
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .style('font-size', '14px')
          .style('font-weight', '700')
          .style('fill', 'white')
          .style('pointer-events', 'none')
          .text('?');
      }

      // Create invisible overlay for hover target (larger than the icon for easier interaction)
      const badgeOverlay = quadrantGroup
        .append('circle')
        .attr('cx', badgeX)
        .attr('cy', badgeY)
        .attr('r', badgeSize + 5)
        .attr('fill', 'transparent')
        .style('cursor', 'help');

      // Create tooltip using HTML div
      if (quadrantDescriptions[quadrant]) {
        const tooltipId = `tooltip-badge-${quadrant.replace(/\s+/g, '-').toLowerCase()}`;

        // First remove any existing tooltip with this ID
        d3.select(`#${tooltipId}`).remove();

        // Create tooltip div and append to body
        const tooltip = d3
          .select('body')
          .append('div')
          .attr('id', tooltipId)
          .attr('class', 'quadrant-description-tooltip')
          .style('position', 'absolute')
          .style('background', 'white')
          .style('padding', '8px 12px')
          .style('border-radius', '4px')
          .style('border', '1px solid #d1d5db')
          .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
          .style('font-size', '12px')
          .style('max-width', '300px')
          .style('z-index', '1000')
          .style('pointer-events', 'none')
          .style('opacity', 0);

        // Calculate tooltip content
        const description = quadrantDescriptions[quadrant];
        const maxLineLength = isSmallScreen ? 25 : 45; // Characters per line
        const words = description.split(' ');
        const lines: string[] = [];
        let currentLine = words[0];

        // Break description into multiple lines
        for (let i = 1; i < words.length; i++) {
          if (currentLine.length + words[i].length + 1 <= maxLineLength) {
            currentLine += ' ' + words[i];
          } else {
            lines.push(currentLine);
            currentLine = words[i];
          }
        }
        lines.push(currentLine);

        // Add content to tooltip
        tooltip.html(lines.join('<br>'));

        // Add hover events
        badgeOverlay
          .on('mouseenter', function (event) {
            // Get the position of the icon in the document
            const iconPos = this.getBoundingClientRect();

            tooltip
              .style('left', `${iconPos.left + window.scrollX}px`)
              .style('top', `${iconPos.bottom + window.scrollY + 5}px`)
              .transition()
              .duration(200)
              .style('opacity', 1);
          })
          .on('mouseleave', function () {
            tooltip.transition().duration(200).style('opacity', 0);
          });
      }

      // Add quadrant label with info icon
      const quadrantLabelGroup = quadrantGroup.append('g').attr('class', 'quadrant-label-group');

      // Increase font size and positioning for quadrant label text
      quadrantLabelGroup
        .append('text')
        .attr('x', centerX)
        .attr('y', centerY - packedData.r * 0.8) // Higher position (was 0.75)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.1em')
        .attr('font-size', '14px') // Significantly reduced to ensure visibility of change
        .attr('font-weight', '700')
        .attr('fill', () => {
          // Use matching text colors
          return quadrantColors[quadrantIndex];
        })
        .text(quadrant);

      // Create circles using stored positions
      const circleGroups = quadrantGroup
        .selectAll('g.bubble-group')
        .data(nodes, (d: any) => d.name)
        .join('g')
        .attr('class', 'bubble-group')
        .attr('data-name', (d: any) => d.name)
        .style('cursor', 'pointer')
        .attr('transform', (d: any) => {
          const pos = positionsRef.current[d.name];
          if (pos) {
            return `translate(${pos.x},${pos.y})`;
          }
          return `translate(0,0)`;
        });

      // Add circle backgrounds with shadow circles for stacked effect
      circleGroups.selectAll('circle').remove();

      // Shadow/background circle for stacked effect
      circleGroups
        .append('circle')
        .attr('class', 'shadow-circle')
        .attr('r', (d: any) => {
          const pos = positionsRef.current[d.name];
          return pos ? pos.r : 0;
        })
        .attr('cy', 3) // Small offset for shadow effect
        .style('fill', '#e2e8f0')
        .style('filter', 'blur(1px)')
        .style('opacity', (d: any) => (highlightedAreas.length === 0 || highlightedAreas.includes(d.name) ? 0.7 : 0.2));

      // Main circle
      const circleElements = circleGroups
        .append('circle')
        .attr('r', (d: any) => {
          const pos = positionsRef.current[d.name];
          return pos ? pos.r : 0;
        })
        .style('fill', 'white')
        .style('stroke', (d: any) => {
          // For null maturity levels, use grey border
          if (d.maturityLevel === null) {
            return '#d1d5db';
          }

          // Use quadrant color for highlighted items, otherwise use faded maturity color
          if (highlightedAreas.length > 0 && highlightedAreas.includes(d.name)) {
            const quadrantColor = getQuadrantColor(d.name);
            return quadrantColor;
          } else {
            const color = getBorderColor(d.maturityLevel);
            // Convert hex to rgba with 0.6 opacity if it's a hex color
            if (color.startsWith('#')) {
              const r = parseInt(color.slice(1, 3), 16);
              const g = parseInt(color.slice(3, 5), 16);
              const b = parseInt(color.slice(5, 7), 16);
              return `rgba(${r}, ${g}, ${b}, 0.6)`;
            }
            return color; // Return original if not hex
          }
        })
        .style('stroke-width', '0.5px')
        .style('opacity', (d: any) => (highlightedAreas.length === 0 || highlightedAreas.includes(d.name) ? 1 : 0.3))
        .style('filter', 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))'); // Add subtle shadow for depth

      // Add transition for color changes only (not position or size)
      circleElements
        .transition()
        .duration(500)
        .ease(d3.easeQuadInOut)
        .style('stroke', (d: any) => {
          // For null maturity levels, use grey border
          if (d.maturityLevel === null) {
            return '#d1d5db';
          }

          // Use quadrant color for highlighted items, otherwise use faded maturity color
          if (highlightedAreas.length > 0 && highlightedAreas.includes(d.name)) {
            const quadrantColor = getQuadrantColor(d.name);
            return quadrantColor;
          } else {
            const color = getBorderColor(d.maturityLevel);
            // Convert hex to rgba with 0.6 opacity if it's a hex color
            if (color.startsWith('#')) {
              const r = parseInt(color.slice(1, 3), 16);
              const g = parseInt(color.slice(3, 5), 16);
              const b = parseInt(color.slice(5, 7), 16);
              return `rgba(${r}, ${g}, ${b}, 0.6)`;
            }
            return color; // Return original if not hex
          }
        });

      // Add a subtle inner circle decorative effect
      circleGroups
        .append('circle')
        .attr('r', (d: any) => {
          const pos = positionsRef.current[d.name];
          return pos ? pos.r * 0.92 : 0; // Slightly smaller inner circle
        })
        .style('fill', 'none')
        .style('stroke', (d: any) => {
          // For null maturity levels, use light grey
          if (d.maturityLevel === null) {
            return '#e5e7eb';
          }

          // Use quadrant color for highlighted items, otherwise use faded maturity color
          if (highlightedAreas.length > 0 && highlightedAreas.includes(d.name)) {
            const quadrantColor = getQuadrantColor(d.name);
            // Make it slightly transparent
            if (quadrantColor.startsWith('#')) {
              const r = parseInt(quadrantColor.slice(1, 3), 16);
              const g = parseInt(quadrantColor.slice(3, 5), 16);
              const b = parseInt(quadrantColor.slice(5, 7), 16);
              return `rgba(${r}, ${g}, ${b}, 0.3)`;
            }
            return quadrantColor;
          } else {
            const color = getBorderColor(d.maturityLevel);
            // Convert hex to rgba with very light opacity for subtle inner ring
            if (color.startsWith('#')) {
              const r = parseInt(color.slice(1, 3), 16);
              const g = parseInt(color.slice(3, 5), 16);
              const b = parseInt(color.slice(5, 7), 16);
              return `rgba(${r}, ${g}, ${b}, 0.15)`;
            }
            return color; // Return original if not hex
          }
        })
        .style('stroke-width', '0.5px')
        .style('opacity', (d: any) => (highlightedAreas.length === 0 || highlightedAreas.includes(d.name) ? 1 : 0.3));

      // Add text labels
      circleGroups
        .append('text')
        .attr('class', 'circle-text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em') // Vertical centering adjustment
        .style('fill', (d: any) => {
          // Use light grey text for null maturity levels
          return d.maturityLevel === null ? '#9ca3af' : '#4b5563';
        })
        .style('pointer-events', 'none')
        .each(function (d: any) {
          const textElement = d3.select(this);
          const pos = positionsRef.current[d.name];
          const radius = pos ? pos.r : 0;

          // Base font size on radius with a minimum for small screens
          const baseMinFontSize = isSmallScreen ? 10 : 8;
          const baseFontSize = Math.max(baseMinFontSize, radius / 4);

          // Calculate available width within the circle
          const availableWidth = radius * 1.7; // 85% of diameter

          // Use temporary text to check if it fits
          const text = d.name;
          const tempText = svg
            .append('text')
            .attr('font-size', `${baseFontSize}px`)
            .text(text)
            .style('visibility', 'hidden');

          const textLength = tempText.node()?.getComputedTextLength() || 0;
          tempText.remove();

          // Set initial font size
          textElement.style('font-size', `${baseFontSize}px`);

          // If text is too long, try to break it
          if (textLength > availableWidth) {
            // Split into words
            const words = text.split(/\s+/);
            const lines: string[] = [];
            let currentLine = words[0];

            // Create lines
            for (let i = 1; i < words.length; i++) {
              const word = words[i];
              const testLine = currentLine + ' ' + word;

              // Create a test element to measure line width
              const testText = svg
                .append('text')
                .attr('font-size', `${baseFontSize}px`)
                .text(testLine)
                .style('visibility', 'hidden');

              const testLineWidth = testText.node()?.getComputedTextLength() || 0;
              testText.remove();

              if (testLineWidth > availableWidth) {
                lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            }
            lines.push(currentLine);

            // Clear the text element
            textElement.text(null);

            // Add each line as a tspan
            const lineHeight = baseFontSize * 1.1;
            const totalHeight = lineHeight * lines.length;

            // Center lines vertically - improved vertical centering
            const startY = -(totalHeight / 2) + lineHeight / 2;

            lines.forEach((line, i) => {
              textElement
                .append('tspan')
                .attr('x', 0)
                .attr('dy', i === 0 ? startY : lineHeight)
                .text(line);
            });

            // If still too many lines, reduce font size
            if (lines.length > 3 || totalHeight > radius * 1.4) {
              const reducedSize = Math.max(baseMinFontSize, baseFontSize * 0.8);
              textElement.style('font-size', `${reducedSize}px`);
            }
          } else {
            // Single line case - better centered vertically
            textElement.text(text).attr('dy', '0.1em'); // Small adjustment for better vertical centering
          }
        });

      // Update the maturity labels in the circles to use squares visualization
      circleGroups.selectAll('.maturity-label').remove(); // Remove existing text labels

      // Create SVG group for maturity visualization
      const maturityLabels = circleGroups
        .append('g')
        .attr('class', 'maturity-label')
        .attr('transform', (d: any) => {
          const pos = positionsRef.current[d.name];
          const radius = pos ? pos.r : 0;
          return `translate(0, -${radius * 0.5})`;
        })
        .style('cursor', 'pointer')
        .style('opacity', (d: any) => (previouslyVisibleLabels.has(d.name) ? 1 : 0))
        .style('pointer-events', (d: any) => (previouslyVisibleLabels.has(d.name) ? 'all' : 'none'));

      // Create the squares for maturity visualization
      maturityLabels.each(function (d: any) {
        const group = d3.select(this);
        const pos = positionsRef.current[d.name];
        const radius = pos ? pos.r : 0;

        // Calculate square size based on circle radius (between 5px and 8px)
        const squareSize = Math.min(8, Math.max(5, radius / 15));
        const gapSize = squareSize * 0.25; // Gap is 25% of square size
        const totalSquares = 4;

        // Determine how many colored squares to show based on level
        let coloredSquares = 0;

        if (d.maturityLevel === null) {
          coloredSquares = 0; // No colored squares for null maturity
        } else if (d.maturityLevel === 'Weak') coloredSquares = 1;
        else if (d.maturityLevel === 'Emerging') coloredSquares = 2;
        else if (d.maturityLevel === 'Established') coloredSquares = 3;
        else if (d.maturityLevel === 'Peak') coloredSquares = 4;

        // Calculate total width to center the squares
        const totalWidth = squareSize * totalSquares + gapSize * (totalSquares - 1);
        const startX = -totalWidth / 2;

        // Create the squares
        for (let i = 0; i < totalSquares; i++) {
          const squareColor =
            d.maturityLevel === null ? '#d1d5db' : i < coloredSquares ? getBorderColor(d.maturityLevel) : '#d1d5db';
          const x = startX + i * (squareSize + gapSize);

          group
            .append('rect')
            .attr('x', x)
            .attr('y', -squareSize / 2)
            .attr('width', squareSize)
            .attr('height', squareSize)
            .attr('fill', squareColor);
        }
      });

      // Move event handlers to the group level instead of just the circle
      circleGroups
        .on('mouseenter', function (event, d: any) {
          // Call onHover callback if provided
          if (onHover) {
            onHover(d.name, true);
          }

          // Show maturity label and track it
          visibleLabelsRef.current.add(d.name);
          d3.select(this).select('.maturity-label').style('opacity', 1).style('pointer-events', 'all');

          // Change circle appearance on hover - more pronounced effects
          d3.select(this)
            .select('circle:nth-of-type(2)') // Main circle (after shadow circle)
            .transition()
            .duration(200)
            .style('stroke-width', '1px')
            .style('filter', 'drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.15))'); // Enhanced shadow on hover

          // Also enhance the shadow circle for better hover effect
          d3.select(this)
            .select('.shadow-circle')
            .transition()
            .duration(200)
            .attr('cy', 5) // Increase offset for more pronounced shadow
            .style('opacity', 0.8) // Darker shadow
            .style('filter', 'blur(2px)'); // More blur

          // Also enhance the inner decorative circle
          d3.select(this)
            .select('circle:nth-of-type(3)') // Inner decorative circle
            .transition()
            .duration(200)
            .style('stroke-width', '0.75px');

          const group = d3.select(this);
          const pos = positionsRef.current[d.name];
          const currentRadius = pos ? pos.r : 0;

          // Only show plus button if not already at Peak and no dialog is open
          if (d.maturityLevel !== 'Peak' && !openDialogRef.current) {
            // Add + button - positioned below the text
            const plusButton = group
              .append('g')
              .attr('class', 'plus-button')
              .style('cursor', 'pointer')
              .attr('transform', `translate(${currentRadius * 0.3}, ${currentRadius * 0.6})`);

            plusButton.append('circle').attr('r', 10).style('fill', 'white');

            plusButton
              .append('text')
              .text('+')
              .attr('text-anchor', 'middle')
              .attr('dy', '0.3em')
              .style('font-size', '14px')
              .style('fill', '#4b5563');

            // Add click handlers for plus button
            plusButton.on('click', event => {
              event.stopPropagation();
              const levels: MaturityLevel[] = ['Weak', 'Emerging', 'Established', 'Peak'];
              const currentIndex = levels.indexOf(d.maturityLevel);
              if (currentIndex < levels.length - 1) {
                onMaturityChange(d.name, levels[currentIndex + 1]);
              }
            });
          }

          // Only show minus button if not already at Weak and no dialog is open
          if (d.maturityLevel !== 'Weak' && !openDialogRef.current) {
            // Add - button - positioned to the left of the + button
            const minusButton = group
              .append('g')
              .attr('class', 'minus-button')
              .style('cursor', 'pointer')
              .attr('transform', `translate(${-currentRadius * 0.3}, ${currentRadius * 0.6})`);

            minusButton.append('circle').attr('r', 10).style('fill', 'white');

            minusButton
              .append('text')
              .text('-')
              .attr('text-anchor', 'middle')
              .attr('dy', '0.3em')
              .style('font-size', '14px')
              .style('fill', '#4b5563');

            // Add click handler for minus button
            minusButton.on('click', event => {
              event.stopPropagation();
              const levels: MaturityLevel[] = ['Weak', 'Emerging', 'Established', 'Peak'];
              const currentIndex = levels.indexOf(d.maturityLevel);
              if (currentIndex > 0) {
                onMaturityChange(d.name, levels[currentIndex - 1]);
              }
            });
          }
        })
        .on('mouseleave', function (this: SVGGElement, event: any, d: any) {
          // Check if mouse moved to a child element (like buttons)
          const relatedTarget = event.relatedTarget;
          if (this.contains(relatedTarget)) {
            return; // Don't hide if hovering over child elements
          }

          // Don't clear hover if dialog is open for this area
          if (openDialogRef.current === d.name) {
            console.log('Ignoring mouseleave because dialog is open for:', d.name);
            return;
          }

          // Call onHover callback with null to indicate no circle is hovered
          if (onHover) {
            console.log('D3CHART HOVER END:', d.name);
            onHover(d.name, false);
          }

          // Hide maturity label
          visibleLabelsRef.current.delete(d.name);
          d3.select(this).select('.maturity-label').style('opacity', 0).style('pointer-events', 'none');

          // Reset circle appearance
          d3.select(this)
            .select('circle:nth-of-type(2)') // Main circle (after shadow circle)
            .transition()
            .duration(200)
            .style('stroke-width', '1px')
            .style('filter', 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))'); // Reset to default shadow

          // Reset inner circle
          d3.select(this)
            .select('circle:nth-of-type(3)') // Inner decorative circle
            .transition()
            .duration(200)
            .style('stroke-width', '1px');

          d3.select(this).selectAll('.plus-button, .minus-button').remove();

          // Reset shadow circle
          d3.select(this)
            .select('.shadow-circle')
            .transition()
            .duration(200)
            .attr('cy', 3) // Reset to original offset
            .style('opacity', highlightedAreas.length === 0 || highlightedAreas.includes(d.name) ? 0.7 : 0.2) // Reset opacity
            .style('filter', 'blur(1px)'); // Reset blur
        })
        .on('click', function (event, d: any) {
          event.stopPropagation();
          const maturityLevels = ['Weak', 'Emerging', 'Established', 'Peak'];

          // Handle null maturity level
          const currentIndex = d.maturityLevel === null ? -1 : maturityLevels.indexOf(d.maturityLevel);

          // Make sure hover state is active for this area
          if (onHover) {
            console.log('Dialog opened, setting hover state for:', d.name);
            openDialogRef.current = d.name;
            onHover(d.name, true);
          }

          // Create and show dialog
          const dialog = d3
            .select('body')
            .append('div')
            .attr('class', 'maturity-dialog')
            .style('position', 'absolute')
            .style('background-color', 'white')
            .style('border-radius', '50%') // Make it circular
            .style('width', '480px') // Increased from 380px
            .style('height', '480px') // Increased from 380px
            .style('display', 'flex')
            .style('flex-direction', 'column')
            .style('justify-content', 'center')
            .style('align-items', 'center')
            .style('padding', '40px') // Increased padding for larger dialog
            .style('box-sizing', 'border-box')
            .style('overflow', 'hidden')
            .style('box-shadow', '0px 6px 16px rgba(0, 0, 0, 0.15)') // Enhanced shadow
            .style('border', '2px solid #e5e7eb') // Increased border thickness to 2px
            .style('z-index', '1000');

          // Calculate the center position of the chart (where all quadrants meet)
          const chartCenterX = dimensions.width / 2;
          const chartCenterY = dimensions.height / 2;

          // Get the bounding rect of the SVG element to calculate the absolute position
          const svgRect = svgRef.current.getBoundingClientRect();
          const centerX = svgRect.left + chartCenterX + window.scrollX;
          const centerY = svgRect.top + chartCenterY + window.scrollY;

          // Position the dialog at the center of the chart
          dialog.style('left', `${centerX}px`).style('top', `${centerY}px`).style('transform', 'translate(-50%, -50%)');

          // Keep track of which maturity level description is being shown
          let viewingLevelIndex = currentIndex;

          // Function to update the maturity level description
          const updateMaturityView = () => {
            const viewingLevel = maturityLevels[viewingLevelIndex];

            // Update the maturity level info section
            const maturitySection = dialog.select('.maturity-section');
            if (!maturitySection.empty()) {
              maturitySection.html(`
                ${
                  d.maturityLevels && d.maturityLevels[viewingLevel]
                    ? `
                  <div style="margin-top: 5px; color: #374151; font-size: 12px; line-height: 1.3; background-color: #f9fafb; padding: 5px 8px; border-radius: 4px; border-left: 3px solid ${getBorderColor(
                    viewingLevel as MaturityLevel,
                  )}; width: 100%; max-height: 65px; overflow-y: auto;">
                    ${d.maturityLevels[viewingLevel]}
                  </div>
                `
                    : `
                  <div style="margin-top: 5px; color: #6b7280; font-size: 12px; font-style: italic; text-align: center; padding: 5px; width: 100%;">
                    No description available for this level
                  </div>
                `
                }
              `);
            }
          };

          // Add a subtle inner circle decorative effect
          dialog
            .append('div')
            .style('position', 'absolute')
            .style('top', '12px')
            .style('left', '12px')
            .style('right', '12px')
            .style('bottom', '12px')
            .style('border-radius', '50%')
            .style('border', '2px solid rgba(0, 0, 0, 0.03)') // Make inner decorative border 2px
            .style('pointer-events', 'none');

          // Add a close button at the top of the dialog
          dialog
            .append('button')
            .style('position', 'absolute')
            .style('top', '15px')
            .style('right', '15px')
            .style('background', 'none')
            .style('border', 'none')
            .style('cursor', 'pointer')
            .style('font-size', '18px')
            .style('color', '#9ca3af')
            .text('×')
            .on('click', event => {
              event.stopPropagation();
              openDialogRef.current = null;
              dialog.remove();
              document.removeEventListener('click', closeDialogOnClickOutside);
              if (onHover) {
                onHover(d.name, false);
              }
            });

          // Create a circular content container
          const contentContainer = dialog
            .append('div')
            .style('width', '100%')
            .style('height', '100%')
            .style('display', 'flex')
            .style('flex-direction', 'column')
            .style('justify-content', 'center')
            .style('align-items', 'center')
            .style('max-height', '400px') // Increased from 320px
            .style('overflow-y', 'auto')
            .style('padding', '0 40px'); // Increased horizontal padding from 20px to 40px

          // Add the header above the content container
          dialog
            .append('div')
            .style('position', 'absolute')
            .style('top', '60px') // Position higher up
            .style('left', '0')
            .style('width', '100%')
            .style('text-align', 'center').html(`
              <h3 style="margin: 0; font-size: 15px; font-weight: 600; text-align: center; max-width: 90%; display: inline-block;">${d.name}</h3>
            `);

          // Initial dialog content (without the header)
          contentContainer.html(`
            ${
              d.description
                ? `
            <div style="margin-bottom: 10px; width: 90%;">
              <div style="color: #374151; font-size: 12px; line-height: 1.3; text-align: center;">${d.description}</div>
            </div>
            `
                : ''
            }
            <div style="margin-bottom: 5px; width: 90%; text-align: left;">
              <div class="current-maturity" style="font-weight: 500; margin-bottom: 5px; font-size: 12px; display: flex; align-items: center; gap: 8px;">Current Maturity: 
                <span style="display: inline-flex; align-items: center;">
                  ${renderMaturityAsSquares(
                    d.maturityLevel as MaturityLevel,
                    getBorderColor(d.maturityLevel as MaturityLevel),
                  )}
                </span>
                <div style="display: flex; gap: 8px; flex-direction: row;">
                  ${
                    d.maturityLevel !== 'Weak' && d.maturityLevel !== null
                      ? `
                    <button class="maturity-button minus-button" style="
                      width: 24px;
                      height: 24px;
                      border-radius: 50%;
                      background: white;
                      border: 1px solid #e5e7eb;
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 14px;
                      color: #4b5563;
                    ">-</button>
                  `
                      : ''
                  }
                  ${
                    d.maturityLevel !== 'Peak'
                      ? `
                    <button class="maturity-button plus-button" style="
                      width: 24px;
                      height: 24px;
                      border-radius: 50%;
                      background: white;
                      border: 1px solid #e5e7eb;
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 14px;
                      color: #4b5563;
                    ">+</button>
                  `
                      : ''
                  }
                </div>
              </div>
            </div>
            <div class="maturity-section" style="margin-bottom: 10px; width: 90%;">
              <!-- Maturity level description will be inserted here by updateMaturityView() -->
            </div>
            <div style="margin-bottom: 10px; width: 90%;">
              ${
                d.allContributors && d.allContributors.length > 0
                  ? `
                <div style="margin-top: 5px; width: 100%;">
                  <div style="display: flex; flex-direction: column; gap: 2px; max-height: 100px; overflow-y: auto; width: 100%;">
                    ${d.allContributors
                      .slice(0, 5)
                      .map(
                        contributor => `
                      <div style="display: flex; justify-content: space-between; font-size: 11px; width: 100%;">
                        <div>${contributor.dimension}</div>
                        <div>${Math.round(contributor.percentage)}%</div>
                      </div>
                    `,
                      )
                      .join('')}
                  </div>
                </div>
              `
                  : d.topContributors && d.topContributors.length > 0
                  ? `
                <div style="margin-top: 5px; width: 100%;">
                  <div style="display: flex; flex-direction: column; gap: 2px; width: 100%;">
                    ${d.topContributors
                      .map(
                        contributor => `
                      <div style="display: flex; justify-content: space-between; font-size: 11px; width: 100%;">
                        <div>${contributor.dimension}</div>
                        <div>${Math.round(contributor.percentage)}%</div>
                      </div>
                    `,
                      )
                      .join('')}
                  </div>
                </div>
              `
                  : ''
              }
            </div>
          `);

          // Add the impact value at the bottom of the dialog
          dialog
            .append('div')
            .style('position', 'absolute')
            .style('bottom', '60px')
            .style('left', '0')
            .style('width', '100%')
            .style('text-align', 'center').html(`
              <div style="font-weight: 500; font-size: 12px; text-align: center;">Impact: <span style="color: #4b5563;">${Math.round(
                d.calculatedImpact,
              )}%</span></div>
            `);

          // Initialize the maturity view
          updateMaturityView();

          // Set up click-outside-to-close behavior
          const closeDialogOnClickOutside = (e: MouseEvent) => {
            const dialogNode = dialog.node();
            if (dialogNode && !dialogNode.contains(e.target as Node)) {
              // First clear the dialog tracking ref
              openDialogRef.current = null;

              // Then remove the dialog from DOM
              dialog.remove();
              document.removeEventListener('click', closeDialogOnClickOutside);

              // Reset hover state when dialog closes
              if (onHover) {
                console.log('Dialog closed by clicking outside, resetting hover state for:', d.name);
                onHover(d.name, false);
              }
            }
          };

          // Use setTimeout to avoid the current click triggering the handler immediately
          setTimeout(() => {
            document.addEventListener('click', closeDialogOnClickOutside);
          }, 0);

          // Add click handlers for the maturity buttons
          const handleMaturityButtonClick = function (event: MouseEvent) {
            event.stopPropagation();
            const isPlusButton = this.classList.contains('plus-button');
            const levels: MaturityLevel[] = ['Weak', 'Emerging', 'Established', 'Peak'];
            const currentIndex = d.maturityLevel ? levels.indexOf(d.maturityLevel) : -1;

            if (isPlusButton) {
              let newLevel: MaturityLevel;
              if (d.maturityLevel === null || currentIndex === -1) {
                // If current maturity is null, start from Weak
                newLevel = 'Weak';
              } else if (currentIndex < levels.length - 1) {
                newLevel = levels[currentIndex + 1];
              } else {
                return; // Already at Peak
              }

              onMaturityChange(d.name, newLevel);
              d.maturityLevel = newLevel; // Update the local data

              // Update the current maturity display
              dialog.select('.current-maturity').html(`
                <div style="font-weight: 500; margin-bottom: 5px; font-size: 12px; display: flex; align-items: center; gap: 8px;">Current Maturity: 
                  <span style="display: inline-flex; align-items: center;">
                    ${renderMaturityAsSquares(newLevel, getBorderColor(newLevel))}
                  </span>
                  <div style="display: flex; gap: 8px; flex-direction: row;">
                    ${
                      newLevel !== 'Weak' && newLevel !== null
                        ? `
                      <button class="maturity-button minus-button" style="
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 1px solid #e5e7eb;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 14px;
                        color: #4b5563;
                      ">-</button>
                    `
                        : ''
                    }
                    ${
                      newLevel !== 'Peak'
                        ? `
                      <button class="maturity-button plus-button" style="
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 1px solid #e5e7eb;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 14px;
                        color: #4b5563;
                      ">+</button>
                    `
                        : ''
                    }
                  </div>
                </div>
              `);

              // Update the maturity description
              dialog.select('.maturity-section').html(`
                ${
                  d.maturityLevels && d.maturityLevels[newLevel]
                    ? `
                  <div style="margin-top: 5px; color: #374151; font-size: 12px; line-height: 1.3; background-color: #f9fafb; padding: 5px 8px; border-radius: 4px; border-left: 3px solid ${getBorderColor(
                    newLevel,
                  )}; width: 100%; max-height: 65px; overflow-y: auto;">
                    ${d.maturityLevels[newLevel]}
                  </div>
                `
                    : `
                  <div style="margin-top: 5px; color: #6b7280; font-size: 12px; font-style: italic; text-align: center; padding: 5px; width: 100%;">
                    No description available for this level
                  </div>
                `
                }
              `);

              // Reattach click handlers to the new buttons
              dialog.selectAll('.maturity-button').on('click', handleMaturityButtonClick);
            } else if (!isPlusButton && d.maturityLevel !== null && currentIndex > 0) {
              const newLevel = levels[currentIndex - 1];
              onMaturityChange(d.name, newLevel);
              d.maturityLevel = newLevel; // Update the local data

              // Update the current maturity display
              dialog.select('.current-maturity').html(`
                <div style="font-weight: 500; margin-bottom: 5px; font-size: 12px; display: flex; align-items: center; gap: 8px;">Current Maturity: 
                  <span style="display: inline-flex; align-items: center;">
                    ${renderMaturityAsSquares(newLevel, getBorderColor(newLevel))}
                  </span>
                  <div style="display: flex; gap: 8px; flex-direction: row;">
                    ${
                      newLevel !== 'Weak' && newLevel !== null
                        ? `
                      <button class="maturity-button minus-button" style="
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 1px solid #e5e7eb;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 14px;
                        color: #4b5563;
                      ">-</button>
                    `
                        : ''
                    }
                    ${
                      newLevel !== 'Peak'
                        ? `
                      <button class="maturity-button plus-button" style="
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 1px solid #e5e7eb;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 14px;
                        color: #4b5563;
                      ">+</button>
                    `
                        : ''
                    }
                  </div>
                </div>
              `);

              // Update the maturity description
              dialog.select('.maturity-section').html(`
                ${
                  d.maturityLevels && d.maturityLevels[newLevel]
                    ? `
                  <div style="margin-top: 5px; color: #374151; font-size: 12px; line-height: 1.3; background-color: #f9fafb; padding: 5px 8px; border-radius: 4px; border-left: 3px solid ${getBorderColor(
                    newLevel,
                  )}; width: 100%; max-height: 65px; overflow-y: auto;">
                    ${d.maturityLevels[newLevel]}
                  </div>
                `
                    : `
                  <div style="margin-top: 5px; color: #6b7280; font-size: 12px; font-style: italic; text-align: center; padding: 5px; width: 100%;">
                    No description available for this level
                  </div>
                `
                }
              `);

              // Reattach click handlers to the new buttons
              dialog.selectAll('.maturity-button').on('click', handleMaturityButtonClick);
            }
          };

          // Attach the click handler to the buttons
          dialog.selectAll('.maturity-button').on('click', handleMaturityButtonClick);
        });

      // Remove previous event handlers from circle elements
      circleElements.on('mouseover', null).on('mouseout', null);

      // Add click handler to maturity label but have it reuse the same dialog logic from the circle
      maturityLabels.on('click', function (event, d: any) {
        event.stopPropagation();
        // Call the parent circle group's click handler to reuse the dialog creation logic
        const parentGroup = d3.select(this.parentNode as Element);
        if (parentGroup && !parentGroup.empty()) {
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          });

          // Dispatch the click event on the parent group
          (parentGroup.node() as Node)?.dispatchEvent(clickEvent);
        }
      });
    });

    // After creating all the elements, restore any previously visible labels
    previouslyVisibleLabels.forEach(name => {
      const circleGroup = svg.selectAll('.bubble-group').filter((d: any) => d.name === name);
      if (!circleGroup.empty()) {
        circleGroup.select('.maturity-label').style('opacity', 1).style('pointer-events', 'all');
      }
    });

    // Add logo in the center of the chart
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    const logoSize = Math.min(dimensions.width, dimensions.height) * 0.16; // 16% of smaller dimension
    const logoCircleSize = logoSize * 1.5; // Circle background slightly larger than logo

    // Add logo background circle
    svg
      .append('circle')
      .attr('cx', centerX)
      .attr('cy', centerY)
      .attr('r', logoCircleSize / 2)
      .style('fill', '#f8f8f8') // Changed from 'white' to a slightly darker off-white color
      .style('stroke', '#e5e7eb')
      .style('stroke-width', '1px')
      .style('filter', `url(#${innerShadowId})`); // Apply inner shadow instead of drop shadow

    svg
      .append('image')
      .attr('href', logoUrl)
      .attr('width', logoSize)
      .attr('height', logoSize)
      .attr('x', centerX - logoSize / 2)
      .attr('y', centerY - logoSize / 2)
      .style('pointer-events', 'none'); // Prevent logo from blocking interactions

    // Cleanup for this quadrant
    return () => {
      svg.selectAll('.plus-button, .minus-button').remove();
    };
  }, [
    areas, // for color/maturity changes
    highlightedAreas,
    relationshipWeights,
    onMaturityChange,
    getBackgroundColor,
    getBorderColor,
    onHover,
    quadrantDescriptions,
    dimensions, // for resize
    isSmallScreen,
    getQuadrantColor,
    quadrantColors,
  ]);

  useEffect(() => {
    if (!svgRef.current) return;

    // Add a global mouse leave handler to ensure all labels are hidden
    // when cursor moves quickly and mouseleave events are missed
    svgRef.current.addEventListener('mouseleave', function () {
      // Hide all maturity labels when mouse leaves SVG
      visibleLabelsRef.current.clear();
      d3.select(svgRef.current).selectAll('.maturity-label').style('opacity', 0).style('pointer-events', 'none');

      // Remove plus/minus buttons
      d3.select(svgRef.current).selectAll('.plus-button, .minus-button').remove();

      // Reset any active hover states
      if (onHover) {
        areas.forEach(area => {
          if (openDialogRef.current !== area.name) {
            onHover(area.name, false);
          }
        });
      }
    });

    // Add a global mousemove handler to check if cursor is over a circle
    // This is an additional safety measure for fast mouse movements
    svgRef.current.addEventListener('mousemove', function (event) {
      const target = document.elementFromPoint(event.clientX, event.clientY);
      if (!target || !target.closest('.bubble-group')) {
        // If mouse isn't over a bubble group, hide any maturity labels
        // that shouldn't be visible (except for areas with open dialogs)
        const labelsToHide = new Set(visibleLabelsRef.current);
        if (openDialogRef.current) {
          labelsToHide.delete(openDialogRef.current);
        }

        labelsToHide.forEach(areaName => {
          visibleLabelsRef.current.delete(areaName);
          d3.select(svgRef.current)
            .selectAll(`.bubble-group[data-name="${areaName}"]`)
            .select('.maturity-label')
            .style('opacity', 0)
            .style('pointer-events', 'none');

          // Call onHover to reset hover state
          if (onHover) {
            onHover(areaName, false);
          }
        });
      }
    });
  }, [
    areas,
    highlightedAreas,
    relationshipWeights,
    onMaturityChange,
    getBackgroundColor,
    getBorderColor,
    onHover,
    quadrantDescriptions,
    dimensions,
    isSmallScreen,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        marginTop: '0',
        marginBottom: '24px',
        width: '90vw',
        height: isSmallScreen ? '70vh' : '90vh',
        maxWidth: '90%',
        margin: '0 auto',
        backgroundColor: 'transparent',
        position: 'relative',
      }}
    >
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent',
          display: 'block',
        }}
      />
    </div>
  );
};
